import {ResponsiveLine} from "@nivo/line";
import { Placeholder } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import ChartTooltip from "./ChartTooltip.jsx";
import { DateTime } from 'luxon';
import { useId } from 'react';

const DEFAULT_CURVE = 'monotoneX';

function TimeSeriesLineChart({title, data, dataSeries, unit, curve = DEFAULT_CURVE, zeroPoint = null, minDate, maxDate, yTickCount = 4, margin = {top: 20, right: 40, bottom: 70, left: 40}, markers= []}) {
  const {t} = useTranslation();
  const graphId = useId();
  
  const ClipLayer = ({ series, lineGenerator, innerWidth, innerHeight }) => {
    return (
      <>
        <clipPath id={`clip-${graphId}`}>
          <rect x={0} y={0} width={innerWidth} height={innerHeight} />
        </clipPath>
        <style>{`#${CSS.escape(graphId)} path, #${CSS.escape(graphId)} circle { clip-path: url(#clip-${CSS.escape(graphId)}); }`}</style>
      </>
    );
  };
  
  if (!data) {
    return (<Placeholder style={{height: '100%', width: '100%'}}/>);
  }

  if (data?.every(series => series.length === 0)) {
    return (
      <div className={"d-flex justify-content-center align-items-center h-100"}>
        <p className={"text-muted fs-6"}>{t('charts.no_recent_data')}</p>
      </div>
    );
  }

  let yMin = undefined;
  let yMax = undefined;

  const chartData = data.map((series, index) => {
    const seriesProperties = dataSeries?.[index] || {};
    return Object.assign({}, {
      id: dataSeries?.[index] || index+1,
      data: series.map(d => {
        const value = parseFloat(d.value);
        if (yMax == undefined || value > yMax) {
          yMax = value;
        }

        if (yMin == undefined || value < yMin) {
          yMin = value;
        }

        return {
          x: DateTime.fromISO(d.timestamp).toJSDate(),
          y: d.value.toString(),
        }
      }),
    }, seriesProperties);
  })

  const maxValue = ((yMax > 0 ? Math.ceil(yMax * 1.1) : Math.ceil(yMax * 0.9)) * 10) / 10;
  let minValue = ((yMin > 0 ? Math.floor(yMin * 0.9) : Math.floor(yMin * 1.1)) * 10) / 10;

  const markersInRange = markers.filter(m => m.axis === 'y' && m.value >= minValue && m.value <= maxValue);

  if(zeroPoint !== null) {
    minValue = minValue > zeroPoint ? zeroPoint : minValue;
  }

  const yTickIncrement = (maxValue - minValue) / yTickCount;
  const yTicks = [...Array(yTickCount + 1).keys()].map(i => Math.round((minValue + (i) * yTickIncrement) * 100) / 100);

  return (
    <div id={graphId} style={{width: '100%', height: '100%'}}> 
      <ResponsiveLine
        animate
        curve={curve}
        enableSlices={'x'}
        margin={margin}
        markers={markersInRange}
        enableArea={true}
        areaBaselineValue={minValue}
        axisBottom={{
          tickValues: 'every 12 hours',
          tickPadding: 5,
          tickRotation: -45,
          format: '%b %d %H:%M',
        }}
        axisLeft={{
          legendOffset: -40,
          tickValues: yTicks,
          tickPadding: 5,
          truncateTickAt: 0
        }}
        data={chartData}
        enableTouchCrosshair
        enablePoints={false}
        colors={['#42708C', '#8C4242', '#8C8B42']}
        gridXValues={[minDate]}
        gridYValues={yTicks}
        xFormat="time:%Y-%m-%dT%H:%M:%S.%fZ"
        Format=" >-.2f"
        xScale={{
          format: '%Y-%m-%dT%H:%M:%S.%fZ',
          type: 'time',
          min: minDate,
          max: maxDate ? maxDate : new Date(),
        }}
        yScale={{
          type: 'linear',
          min: minValue,
          max: maxValue,
        }}
        sliceTooltip={({slice}) => {
          const point = slice.points[0];
          return (<ChartTooltip point={point} unit={unit}/>);
        }}
        layers={[
          "grid",
          "markers",
          "axes",
          "areas",
          "crosshair",
          ClipLayer,
          "lines",
          "points",
          "slices",
          "mesh",
          "legends",
        ]}
      />
    </div>
  )
}

export default TimeSeriesLineChart

